/* global Word */
import type { TemplateProfile } from '../templates';
import type { ElementDefinition } from './types';
import { getNextExhibitNumber, renumberExhibitTables, setExhibitHeaderText } from '../word/exhibit/renumbering';
import { TableBuilder } from '../word/builders/TableBuilder';
import { ContentControlBuilder } from '../word/builders/ContentControlBuilder';

const CM = (v: number) => Math.round(v * 28.3465);

// Fallback constants used only when profile.tableLayout is absent (should not happen).
const FALLBACK_W    = CM(18.40);  // matches layouts.full.colWidth = Text Full width
const FALLBACK_R1_H = CM(0.49);
const FALLBACK_R2_H = CM(2.00);
const FALLBACK_R3_H = CM(0.49);

// Paste-area background — white so the drop zone blends with the page
const PASTE_BG = '#ffffff';

function toHex(color: string): string {
  return color.startsWith('#') ? color : `#${color}`;
}

function clearTableBorders(table: Word.Table): void {
  const tableBorderLocs: Word.BorderLocation[] = [
    Word.BorderLocation.top,
    Word.BorderLocation.bottom,
    Word.BorderLocation.left,
    Word.BorderLocation.right,
    Word.BorderLocation.insideHorizontal,
    Word.BorderLocation.insideVertical,
  ];
  tableBorderLocs.forEach((loc) => {
    table.getBorder(loc).type = Word.BorderType.none;
  });
}

function clearCellBorders(cell: Word.TableCell): void {
  const cellBorderLocs: Word.BorderLocation[] = [
    Word.BorderLocation.top,
    Word.BorderLocation.bottom,
    Word.BorderLocation.left,
    Word.BorderLocation.right,
  ];
  cellBorderLocs.forEach((loc) => {
    cell.getBorder(loc).type = Word.BorderType.none;
  });
}

function zeroPadding(cell: Word.TableCell): void {
  cell.setCellPadding(Word.CellPaddingLocation.top, 0);
  cell.setCellPadding(Word.CellPaddingLocation.bottom, 0);
  cell.setCellPadding(Word.CellPaddingLocation.left, 0);
  cell.setCellPadding(Word.CellPaddingLocation.right, 0);
}

/**
 * Insert a Table Box — a 3-row, 1-column container for pasted tables/images.
 * Dimensions (width, indent, row heights) are driven by profile.tableLayout so
 * each template can define its own layout.
 */
export async function insertTableElement(profile: TemplateProfile): Promise<void> {
  await Word.run(async (ctx) => {
    const selection = ctx.document.getSelection();
    const nextExhibitNumber = await getNextExhibitNumber(ctx);

    // Resolve dimensions from profile.tableLayout (with fallback for safety)
    const tl = profile.tableLayout ?? {
      totalWidth: FALLBACK_W,
      row1Height: FALLBACK_R1_H,
      row2Height: FALLBACK_R2_H,
      row3Height: FALLBACK_R3_H,
    };

    // ── 1. Create 3 × 1 table, fixed width ───────────────────────────────────
    const table = selection.insertTable(3, 1, Word.InsertLocation.after);
    clearTableBorders(table);
    table.autoFitBehavior('FixedSize');
    table.width = tl.totalWidth;
    table.alignment = Word.Alignment.left;

    // Apply left indent when the template places the table in a right column.
    // MUST be called LAST — applyIndent invalidates the table JS reference.
    // Store the indent value now; actual application happens after all setup.
    const leftIndentPt = tl.leftIndent;

    // ── 2. Row heights ────────────────────────────────────────────────────────
    table.rows.load('items');
    await ctx.sync();

    const [r0, r1, r2] = table.rows.items;
    r0.setHeight(tl.row1Height, Word.RowHeightRule.atLeast);
    r1.setHeight(tl.row2Height, Word.RowHeightRule.atLeast);  // grows with paste content
    r2.setHeight(tl.row3Height, Word.RowHeightRule.atLeast);

    // ── 3. Load all cells ─────────────────────────────────────────────────────
    table.rows.load('items');
    await ctx.sync();

    const [row0, row1, row2] = table.rows.items;
    row0.cells.load('items');
    row1.cells.load('items');
    row2.cells.load('items');
    await ctx.sync();

    // ── 4. Row 0 — Header ─────────────────────────────────────────────────────
    const headerCell = row0.cells.items[0];
    zeroPadding(headerCell);
    clearCellBorders(headerCell);
    headerCell.verticalAlignment = Word.VerticalAlignment.top;

    headerCell.body.paragraphs.load('items');
    await ctx.sync();

    const headerPara = headerCell.body.paragraphs.items[0];
    headerPara.alignment   = Word.Alignment.left;
    headerPara.spaceBefore = 0;
    headerPara.spaceAfter  = 0;
    headerPara.leftIndent  = 0;
    setExhibitHeaderText(headerPara, nextExhibitNumber, profile);

    // ── 5. Row 1 — Paste area (with content control placeholder) ─────────────
    const pasteCell = row1.cells.items[0];
    zeroPadding(pasteCell);
    clearCellBorders(pasteCell);
    pasteCell.verticalAlignment = Word.VerticalAlignment.top;
    pasteCell.shadingColor      = PASTE_BG;  // visual cue — drop zone

    pasteCell.body.paragraphs.load('items');
    await ctx.sync();

    const pastePara = pasteCell.body.paragraphs.items[0];
    pastePara.clear();
    pastePara.alignment = Word.Alignment.left;
    
    let placeholderText = 'Paste your Table/Image here';
    if (tl) {
      const h = (tl.row2Height / 28.3465).toFixed(2);
      const w = (tl.totalWidth / 28.3465).toFixed(2);
      placeholderText = `Paste your Table/Image here (${h}*${w} cm)`;
    }
    ContentControlBuilder.insertTablePlaceholder(pastePara, placeholderText);

    // ── 6. Row 2 — Source ─────────────────────────────────────────────────────
    const sourceCell = row2.cells.items[0];
    zeroPadding(sourceCell);
    clearCellBorders(sourceCell);
    sourceCell.verticalAlignment = Word.VerticalAlignment.center;

    sourceCell.body.paragraphs.load('items');
    await ctx.sync();

    const sourcePara = sourceCell.body.paragraphs.items[0];
    sourcePara.alignment   = Word.Alignment.left;
    sourcePara.spaceBefore = 2.5;  // breathing room from the table border
    sourcePara.spaceAfter  = 0;
    sourcePara.leftIndent  = 0;

    const sourceRun = sourcePara.insertText('Source: source text here', Word.InsertLocation.start);
    sourceRun.font.name  = profile.typography.fontFamily;
    sourceRun.font.size  = profile.typography.sourceFontSize;
    sourceRun.font.bold  = false;
    sourceRun.font.color = toHex(profile.colorPalette.sourceText);

    await renumberExhibitTables(ctx, profile);

    // ── 7. Park cursor below the new table ────────────────────────────────────
    const newPara = selection.insertParagraph('', Word.InsertLocation.after);
    newPara.select();
    await ctx.sync();

    // ── 8. Apply left indent LAST (invalidates table reference) ───────────────
    if (leftIndentPt && leftIndentPt > 0) {
      if (profile.id === 'template-2') {
        table.alignment = Word.Alignment.right;
        await ctx.sync();
      } else {
        await TableBuilder.applyIndent(ctx, table, leftIndentPt);
      }
    }
  });
}

/** Element definition for table box insertion. */
export const tableElement: ElementDefinition = {
  id: 'table-box',
  label: 'Table Box',
  icon: 'TableRegular',
  description: 'Full-width box for pasted tables and images',
  category: 'document',
  insertAsync: insertTableElement,
};
